import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Video } from "lucide-react";
import { ParticipantTile, type TrackReferenceOrPlaceholder } from "@livekit/components-react";

import { leaveBoardFullscreen } from "../../application/boardFullscreen";
import {
    fullscreenFaces,
    TOOL_BOARD_CLOSED_MESSAGE,
    TOOL_BOARD_FULLSCREEN_MESSAGE,
    TOOL_BOARD_FULLSCREEN_STATE_MESSAGE,
    type ToolBoardKind,
} from "../../domain/meetingToolBoard";

type Props = {
    kind: ToolBoardKind;
    /** Same-origin page this screen frames: the therapist's board or the patient's. */
    src: string;
    /** Everybody's camera, to keep the faces on screen beside the board. */
    faces: TrackReferenceOrPlaceholder[];
    /** Who the room is listening to, kept on screen when a fullscreen board has no room for everybody. */
    speaker?: string | null;
    /** Put the board away for the whole call. Therapist only. */
    onClose?: () => void;
    /**
     * The board's own session ended inside the frame: the therapist ended
     * the constellation, or — on the patient's side — the board says it has
     * been finished. Either way this screen goes back to the call.
     */
    onEnded?: () => void;
};

/**
 * A tool's board in the call, with the faces kept beside it.
 *
 * Framed rather than drawn in place: both boards are whole pages, laid out for
 * a screen of their own, with overlays pinned to the viewport. A frame gives
 * each one exactly that screen — the slot on the stage — without either page
 * having to learn it can be shown somewhere smaller.
 *
 * The faces stay up the whole time. A constellation is set up while watching
 * what it does to the person setting it, and a therapist who has to leave the
 * board to see the patient's face has lost the moment they were looking for.
 * They run down the side where the screen is wide enough to spare a column,
 * and along the bottom where it is not — fullscreen included: the board's own
 * fullscreen control asks this screen, which puts the faces up with it.
 */
export default function MeetingToolBoard({ kind, src, faces, speaker = null, onClose, onEnded }: Props) {
    const { t } = useTranslation();
    const frameRef = useRef<HTMLIFrameElement | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [fullscreen, setFullscreen] = useState(false);

    /*
     * The board's fullscreen, run from here. Fullscreen inside the frame would
     * fill the screen with the board alone; the board and the faces go up
     * together instead, and the frame is told, so its control reads right.
     * The click that asked still counts: a gesture inside a frame activates
     * the page around it too.
     */
    useEffect(() => {
        const tellFrame = () => {
            const active = document.fullscreenElement === rootRef.current;
            setFullscreen(active);
            frameRef.current?.contentWindow?.postMessage(
                { type: TOOL_BOARD_FULLSCREEN_STATE_MESSAGE, active },
                window.location.origin,
            );
        };
        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (event.source !== frameRef.current?.contentWindow) return;
            if (!isRecord(event.data) || event.data.type !== TOOL_BOARD_FULLSCREEN_MESSAGE) return;
            const root = rootRef.current;
            if (!root) return;
            if (event.data.on === true) {
                if (document.fullscreenElement !== root) {
                    void root.requestFullscreen?.().catch(tellFrame);
                }
            } else if (document.fullscreenElement === root) {
                void document.exitFullscreen?.().catch(() => undefined);
            }
        };
        window.addEventListener("message", onMessage);
        document.addEventListener("fullscreenchange", tellFrame);
        return () => {
            window.removeEventListener("message", onMessage);
            document.removeEventListener("fullscreenchange", tellFrame);
        };
    }, []);

    const shownFaces = fullscreen
        ? fullscreenFaces(
              faces,
              speaker,
              (track) => track.participant.identity,
              (track) => track.participant.isLocal,
          )
        : faces;

    /*
     * Ending the board's own session from inside it also puts it away here,
     * so nobody is left looking at a board that has finished.
     */
    useEffect(() => {
        if (!onEnded) return;
        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (event.source !== frameRef.current?.contentWindow) return;
            if (isRecord(event.data) && event.data.type === TOOL_BOARD_CLOSED_MESSAGE) {
                // Out of fullscreen first, then off the stage.
                void leaveBoardFullscreen().then(onEnded);
            }
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [onEnded]);

    /*
     * Fallback for a board taken away by any other road: out of fullscreen
     * before the frame leaves the page, not after — a layout effect's cleanup
     * runs before React removes the element.
     */
    useLayoutEffect(() => {
        const frame = frameRef.current;
        const root = rootRef.current;
        return () => {
            const element = document.fullscreenElement;
            if (element && (element === frame || element === root)) {
                void document.exitFullscreen?.().catch(() => undefined);
            }
        };
    }, []);

    const title = t(`meetingToolBoard.title.${kind}`);

    return (
        <div
            ref={rootRef}
            className={`flex size-full min-h-0 flex-col gap-2 lg:flex-row ${
                fullscreen ? "bg-surface-base p-2" : ""
            }`}
            data-testid="meeting-tool-board"
            data-fullscreen={fullscreen ? "true" : undefined}
            // Read by the room around the stage, which drops its width cap.
            data-tool-board=""
        >
            <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-xl border border-room-line bg-surface-base">
                <iframe
                    ref={frameRef}
                    // A new board is a new page: remount rather than navigate
                    // the old frame, whose history would hold the last one.
                    key={src}
                    src={src}
                    title={title}
                    allow="fullscreen; clipboard-write"
                    className="size-full border-0"
                    data-testid="meeting-tool-board-frame"
                />
            </div>

            <div
                className="flex shrink-0 gap-2 overflow-x-auto lg:w-48 lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden"
                data-testid="meeting-tool-board-faces"
            >
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="room-control shrink-0 justify-center"
                        data-testid="meeting-tool-board-close"
                    >
                        <Video size={16} aria-hidden="true" />
                        <span>{t("meetingToolBoard.close")}</span>
                    </button>
                )}
                {shownFaces.map((track) => (
                    <div
                        key={`${track.participant.identity}:${track.source}`}
                        className="aspect-video w-32 shrink-0 overflow-hidden rounded-lg border border-room-line shadow-lg sm:w-40 lg:w-full"
                    >
                        <ParticipantTile trackRef={track} className="pointer-events-none size-full" />
                    </div>
                ))}
            </div>
        </div>
    );
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}
